/**
 * Thin oidc-client-ts wrapper for the Zitadel PKCE login.
 *
 * Authorization Code + PKCE against Zitadel. The tenant's org is chosen at login
 * time by appending the org-scoping scope (resolved from the slug via
 * /auth/zitadel/tenant-lookup), so the user lands on their org's login. The
 * resulting access token is sent to the backend as a bearer (see fetchClient +
 * ZitadelBearerAuthentication); the backend resolves the org → our tenant.
 *
 * Sessions stay alive via the refresh-token grant. We request `offline_access`
 * (the naas-web app already has the Refresh Token grant enabled in
 * deploy/zitadel/setup.sh), so `automaticSilentRenew` refreshes the access token
 * before it expires using the refresh token — NOT a cross-subdomain prompt=none
 * iframe (app.* embedding id.* is third-party and blocked by modern browsers).
 * This is the same approach the sibling cypress_ncm_v2 / packetsm apps use; the
 * three hardening details below are what make refresh tokens safe here.
 */
import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts";

import { zitadelConfig } from "./zitadelConfig";

// Authorize-time-only scope: Zitadel accepts it at /authorize but answers
// `invalid_scope` to it on POST /oauth/v2/token (the refresh grant), so it MUST
// be dropped from refreshTokenAllowedScope below or every silent renew 400s.
const PROJECT_ROLES_SCOPE = "urn:zitadel:iam:org:project:roles";

// The audience scopes that put the naas-api client id in the token's `aud`.
// These MUST survive a refresh or the refreshed token stops being accepted by
// the backend (they're also appended per-login in scopeForOrg).
const AUD_SCOPES = [
  zitadelConfig.apiAudience &&
    `urn:zitadel:iam:org:project:id:${zitadelConfig.apiAudience}:aud`,
  zitadelConfig.projectId &&
    `urn:zitadel:iam:org:project:id:${zitadelConfig.projectId}:aud`,
].filter((s): s is string => Boolean(s));

// Requested at /authorize. `offline_access` asks Zitadel for a refresh token so
// automaticSilentRenew can use the refresh-token grant.
const BASE_SCOPE = ["openid", "profile", "email", "offline_access", PROJECT_ROLES_SCOPE].join(
  " ",
);

// Scopes replayed on the refresh-token grant. oidc-client-ts otherwise replays
// the full authorize scope verbatim, and Zitadel 400s `invalid_scope` on
// project:roles AND the per-org scope (both authorize-only) — which killed silent
// renew and logged the user out the moment the access token expired (~12h). Keep
// openid/profile/email + offline_access + the :aud scopes; roles and tenant
// claims survive regardless because Zitadel's claims Action writes them by role
// grant, not by requested scope.
const refreshTokenAllowedScope = [
  "openid",
  "profile",
  "email",
  "offline_access",
  ...AUD_SCOPES,
].join(" ");

let _mgr: UserManager | null = null;
let _token: string | null = null;

/**
 * One-time cleanup for the "No matching state found in storage" sign-in failure.
 * The transient per-signin `state` used to live in localStorage (oidc-client-ts
 * default — we only overrode `userStore`). Failed silent renews / abandoned
 * sign-ins leave orphaned `oidc.<guid>` entries that accumulate until the store
 * is bloated enough that new signin states can't be matched. We now keep state
 * in sessionStorage; this purges the junk already stuck in localStorage but
 * leaves the `oidc.user:` token entry, so a signed-in user is not logged out.
 */
function purgeOrphanedSigninState(): void {
  try {
    const ls = window.localStorage;
    const doomed: string[] = [];
    for (let i = 0; i < ls.length; i++) {
      const key = ls.key(i);
      if (key && key.startsWith("oidc.") && !key.startsWith("oidc.user:")) doomed.push(key);
    }
    doomed.forEach((k) => ls.removeItem(k));
  } catch {
    /* private mode / disabled storage — nothing to clean */
  }
}

function mgr(): UserManager {
  if (!_mgr) {
    purgeOrphanedSigninState();
    const m = new UserManager({
      authority: zitadelConfig.authority,
      client_id: zitadelConfig.clientId,
      redirect_uri: zitadelConfig.redirectUri,
      post_logout_redirect_uri:
        (typeof window !== "undefined" ? window.location.origin : "") + "/login",
      response_type: "code",
      scope: BASE_SCOPE,
      // Without this, oidc-client-ts replays `scope` on the refresh grant and
      // Zitadel 400s invalid_scope (project:roles / org:id are authorize-only),
      // killing silent renew and logging the user out at token expiry.
      refreshTokenAllowedScope,
      automaticSilentRenew: true,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      // sessionStorage (not the localStorage default): survives the same-tab
      // redirect to Zitadel and back, but can't accumulate orphans over time.
      stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
    });

    // Keep the sync bearer cache in step with every renewal — both the background
    // automaticSilentRenew timer and on-demand getFreshAccessToken fire this.
    m.events.addUserLoaded((user) => {
      _token = user.access_token ?? null;
    });
    m.events.addUserUnloaded(() => {
      _token = null;
    });

    // Coordinate EVERY silent renew through one in-flight promise. Zitadel rotates
    // the refresh token on each use and revokes the WHOLE token family if it sees
    // the previous token reused → an instant logout. Two concurrent renews (the
    // background timer + an on-demand refresh when a laptop wakes) would trigger
    // exactly that. Single-flight means the refresh token is only ever spent once.
    // automaticSilentRenew calls this same instance method, so both share it.
    const _signinSilent = m.signinSilent.bind(m);
    let inFlight: Promise<User | null> | null = null;
    m.signinSilent = ((args?: Parameters<typeof _signinSilent>[0]) => {
      if (!inFlight) inFlight = _signinSilent(args).finally(() => (inFlight = null));
      return inFlight;
    }) as typeof m.signinSilent;

    _mgr = m;
  }
  return _mgr;
}

function scopeForOrg(orgId: string): string {
  return [BASE_SCOPE, `urn:zitadel:iam:org:id:${orgId}`, ...AUD_SCOPES].join(" ");
}

/** Redirect to Zitadel to sign in, scoped to the given org.
 *
 * `prompt: "login"` forces the username/password screen every time instead of
 * Zitadel's session/account picker, so a returning user always sees a clean
 * username field (matches the Cypress-NCM behaviour). */
export async function startZitadelLogin(orgId: string): Promise<void> {
  await mgr().signinRedirect({ scope: scopeForOrg(orgId), prompt: "login" });
}

/** Complete the redirect callback; caches the access token. */
export async function completeZitadelLogin(): Promise<void> {
  const user = await mgr().signinRedirectCallback();
  _token = user.access_token ?? null;
}

/** Re-load a persisted token on app start, silently renewing it via the refresh
 * token if the access token has already expired (instead of dropping the
 * session). Primes the sync bearer cache before the first /auth/me. */
export async function rehydrateZitadelToken(): Promise<void> {
  await getFreshAccessToken();
}

/**
 * A valid bearer token, refreshing on demand (refresh-token grant) if the current
 * one is missing/expired. Covers the cases the background automaticSilentRenew
 * timer misses — a backgrounded/throttled tab, a slept machine. Concurrent
 * callers share one in-flight renew (single-flight signinSilent) so the rotating
 * refresh token is never spent twice. Returns null when there's no session or the
 * renew fails.
 */
export async function getFreshAccessToken(): Promise<string | null> {
  try {
    const user = await mgr().getUser();
    if (user && !user.expired && user.access_token) {
      _token = user.access_token;
      return _token;
    }
    // No stored session at all (e.g. the public login page) — nothing to renew.
    // Return null immediately instead of firing a signinSilent that just hangs
    // ~10s on the silent-request timeout and blocks every public request.
    if (!user) {
      _token = null;
      return null;
    }
    // A stored-but-expired session → one shared silent refresh.
    const renewed = await mgr().signinSilent().catch(() => null);
    _token = renewed?.access_token ?? null;
    return _token;
  } catch {
    return null;
  }
}

/** Current bearer token (sync), last known — may be stale/expired. Prefer
 * getFreshAccessToken for anything that must not send an expired token. */
export function getZitadelToken(): string | null {
  return _token;
}

/** Clear the local session (does not hit Zitadel's end-session endpoint). */
export async function zitadelLogout(): Promise<void> {
  _token = null;
  try {
    await mgr().removeUser();
  } catch {
    /* nothing stored — ignore */
  }
}
